<script lang="ts" setup>
  import type { InputCustomEvent } from '@ionic/vue'
  import { maskito as vMaskito } from '@maskito/vue'
  import type { MaskitoOptions, MaskitoElementPredicate } from '@maskito/core'
  import { maskitoDateOptionsGenerator } from '@maskito/kit'
  import { calendarClearOutline } from 'ionicons/icons'
  import GInput from '~/components/common/GInput/GInput.vue'

  const props = withDefaults(
    defineProps<{
      disabled?: boolean
      label: string
      name: string
      minDate?: Date
      maxDate?: Date
    }>(),
    {
      disabled: false,
      minDate: undefined,
      maxDate: undefined
    }
  )

  const emits = defineEmits<{
    (event: 'blur'): void
    (event: 'change', value?: string | number | null): void
    (event: 'focus'): void
    (event: 'input', value?: string | number | null): void
  }>()

  // use `toRef` to create reactive references to `name` prop which is passed to `useField`
  // this is important because vee-validate needs to know if the field name changes
  // https://vee-validate.logaretm.com/v4/guide/composition-api/caveats
  const name = toRef(props, 'name')
  const { value, errorMessage, handleBlur, handleChange } = useField<string | undefined>(name)
  const inputElement = ref<InstanceType<typeof GInput> | null>(null)
  defineExpose({ inputElement })

  function onBlur(event?: InputCustomEvent<FocusEvent>) {
    emits('blur')
    handleBlur(event, true)
  }

  function onChange(event?: InputCustomEvent) {
    emits('change', event?.target.value)
    handleChange(event?.target.value)
  }

  function onInput(event?: InputCustomEvent) {
    emits('input', event?.target.value)
    handleChange(event?.target.value, !!errorMessage.value)
  }

  const mask = maskitoDateOptionsGenerator({
    mode: 'dd/mm/yyyy',
    separator: '/',
    min: props.minDate ? new Date(props.minDate) : undefined,
    max: props.maxDate ? new Date(props.maxDate) : undefined
  })
  const dateOptions: MaskitoOptions & {
    elementPredicate?: MaskitoElementPredicate
  } = {
    ...mask,
    elementPredicate: (el: HTMLElement) => {
      return new Promise((resolve) => {
        requestAnimationFrame(async () => {
          const input = await (el as HTMLIonInputElement).getInputElement()
          resolve(input)
        })
      })
    }
  }
</script>

<template>
  <GInput
    v-maskito="dateOptions"
    placeholder="DD/MM/YYYY"
    :label="label"
    :name="name"
    class="g-date-input-field"
    type="text"
    inputmode="numeric"
    :show-custom-icon="true"
    :icon="calendarClearOutline"
    :value="value"
    :disabled="disabled"
    :error-message="errorMessage"
    @blur="onBlur"
    @change="onChange"
    @focus="$emit('focus')"
    @input="onInput"
  />
</template>
